import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { api } from './api';
import {
  accessoriesText,
  amount,
  decimalInput,
  plain,
  QTY_DECIMALS,
  type LineAccessory,
} from './ui';

export interface QuantityLine {
  id: string;
  name: string;
  spec: string;
  unit: string;
  ordered: string | number;
  done: string | number;
  remaining: string | number;
  /** This enterprise's SKU the line takes stock from; set for outgoing movements. */
  skuId?: string;
  /** Shipped with the product as a whole; named here so the warehouse packs them too. */
  accessories?: LineAccessory[];
}

interface Props {
  title: string;
  subtitle: string;
  /** What this movement is called, e.g. 入库 / 出库 / 发货. */
  verb: string;
  lines: QuantityLine[];
  warehouses: { value: string; label: string }[];
  footnote: string;
  submit: (
    warehouseId: string,
    lines: { lineId: string; quantity: string }[],
    key: string,
  ) => Promise<unknown>;
  onClose: () => void;
  onDone: () => void;
}

/** One warehouse, a quantity per order line, one submission that posts all lines together. */
export default function LineQuantityDialog(p: Props) {
  const [warehouseId, setWarehouseId] = useState(p.warehouses[0]?.value ?? '');
  // Default to everything still outstanding; the user trims it to what actually moved.
  const [quantities, setQuantities] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      p.lines.map((l) => [l.id, Number(l.remaining) > 0 ? plain(l.remaining, QTY_DECIMALS) : '']),
    ),
  );
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [operationKey] = useState(() => crypto.randomUUID());
  const filled = p.lines.filter((l) => Number(l.remaining) > 0 && Number(quantities[l.id]) > 0);
  // Outgoing lines show what the chosen warehouse can give, so a shortfall shows before submit.
  const outgoing = p.lines.some((l) => l.skuId);
  const [balances, setBalances] = useState<any[] | null>(null);
  useEffect(() => {
    if (!outgoing) return;
    api<any[]>('/inventory')
      .then(setBalances)
      .catch(() => setBalances([]));
  }, [outgoing]);
  const availableOf = (line: QuantityLine) =>
    balances
      ? Number(
          balances.find(
            (b) =>
              b.sku_id === line.skuId &&
              b.warehouse_id === warehouseId &&
              b.quality === 'AVAILABLE',
          )?.available ?? 0,
        )
      : null;
  const shortOf = (line: QuantityLine) => {
    const available = availableOf(line);
    return available !== null && Number(quantities[line.id] || 0) > available;
  };
  const shortLines = filled.filter(shortOf);

  async function submit() {
    if (busy) return;
    if (!warehouseId) return setError('请选择本企业仓库');
    if (!filled.length) return setError(`请至少填写一个商品的本次${p.verb}数量`);
    setBusy(true);
    setError('');
    try {
      await p.submit(
        warehouseId,
        filled.map((l) => ({ lineId: l.id, quantity: quantities[l.id] })),
        operationKey,
      );
      p.onDone();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open fullWidth maxWidth="md" onClose={() => !busy && p.onClose()}>
      <DialogTitle sx={{ pb: 1 }}>
        {p.title}
        <Typography color="text.secondary" sx={{ fontSize: 13, mt: 0.5 }}>
          {p.subtitle}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {!error && shortLines.length > 0 && (
            <Alert severity="warning">
              库存不足：
              {shortLines
                .map(
                  (l) =>
                    `${l.name} 需 ${amount(quantities[l.id])} ${l.unit}，可用 ${amount(availableOf(l))} ${l.unit}`,
                )
                .join('；')}
              。可换仓库、改小本次数量分批发，或先补库存。
            </Alert>
          )}
          <TextField
            select
            label="本企业仓库"
            value={warehouseId}
            disabled={busy}
            onChange={(e) => setWarehouseId(e.target.value)}
            sx={{ maxWidth: 320 }}
          >
            {p.warehouses.map((w) => (
              <MenuItem key={w.value} value={w.value}>
                {w.label}
              </MenuItem>
            ))}
          </TextField>
          <Table size="small" sx={{ '& th': { whiteSpace: 'nowrap' } }}>
            <TableHead>
              <TableRow>
                <TableCell>商品</TableCell>
                <TableCell align="right">约定</TableCell>
                <TableCell align="right">已{p.verb}</TableCell>
                <TableCell align="right">待{p.verb}</TableCell>
                {outgoing && <TableCell align="right">可用库存</TableCell>}
                <TableCell sx={{ width: 180 }}>本次{p.verb}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {p.lines.map((line) => (
                <TableRow key={line.id}>
                  <TableCell>
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{line.name}</Typography>
                    <Typography color="text.secondary" sx={{ fontSize: 12 }}>
                      {line.spec}
                    </Typography>
                    {!!line.accessories?.length && Number(quantities[line.id]) > 0 && (
                      <Typography color="text.secondary" sx={{ fontSize: 12, mt: 0.25 }}>
                        随附：{accessoriesText(line.accessories, quantities[line.id])}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">{amount(line.ordered)}</TableCell>
                  <TableCell align="right">{amount(line.done)}</TableCell>
                  <TableCell align="right">{amount(Math.max(0, Number(line.remaining)))}</TableCell>
                  {outgoing && (
                    <TableCell
                      align="right"
                      sx={{
                        color: shortOf(line) ? 'error.main' : 'text.primary',
                        fontWeight: shortOf(line) ? 650 : 400,
                      }}
                    >
                      {availableOf(line) === null ? '…' : amount(availableOf(line))}
                    </TableCell>
                  )}
                  <TableCell>
                    {Number(line.remaining) <= 0 ? (
                      <Typography color="success" sx={{ fontSize: 13 }}>
                        已完成
                      </Typography>
                    ) : (
                      <TextField
                        size="small"
                        error={shortOf(line)}
                        helperText={shortOf(line) ? '超过可用库存' : undefined}
                        value={quantities[line.id] ?? ''}
                        disabled={busy}
                        placeholder="0"
                        inputMode="decimal"
                        onChange={(e) =>
                          setQuantities((q) => ({
                            ...q,
                            [line.id]: decimalInput(e.target.value, QTY_DECIMALS),
                          }))
                        }
                        slotProps={{
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">{line.unit}</InputAdornment>
                            ),
                          },
                        }}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography color="text.secondary" sx={{ fontSize: 12 }}>
            留空或填 0 的商品本次不登记。{p.footnote}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2.5, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button disabled={busy} onClick={p.onClose}>
          取消
        </Button>
        <Button variant="contained" disabled={busy || !filled.length} onClick={submit}>
          {busy
            ? '正在提交…'
            : `确认${p.verb}${filled.length > 1 ? `（${filled.length} 种）` : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
